// src/ui/ExportChatModal.jsx — export this conversation, and the history of
// every export made from any device.

import React, { useEffect, useState } from 'react';
import { X, Download, Doc, Check, Trash } from './icons';
import { exportChat } from '../services/chatExport';
import { watchExports, deleteExportRecord, humanBytes } from '../services/exportHistory';

const when = (ms) => new Date(Number(ms) || 0).toLocaleString(undefined, {
  day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

export default function ExportChatModal({ chat, chatTitle, myUid, msgs, nameOf, onClose }) {
  const [rows, setRows] = useState([]);
  const [scope, setScope] = useState('this');   // this | all
  const [fmt, setFmt] = useState({ html: true, txt: true });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => watchExports(myUid, setRows), [myUid]);
  const shown = scope === 'this' ? rows.filter((r) => r.chatId === chat.id) : rows;

  const go = async () => {
    const formats = Object.keys(fmt).filter((k) => fmt[k]);
    if (!formats.length) return;
    setBusy(true); setErr(''); setDone(null);
    try {
      const r = await exportChat({ chat, chatName: chatTitle, msgs, myUid, nameOf, formats });
      setDone(r);
    } catch { setErr('The export didn’t finish — try again.'); }
    setBusy(false);
  };

  return (
    <div className="ng-modal" onClick={onClose}>
      <div className="ng-card ex-card" onClick={(e) => e.stopPropagation()}>
        <div className="ng-head">
          <b><Download width={16} height={16} /> Export chat</b>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X /></button>
        </div>

        <div className="ng-body">
          <div className="ex-lead">
            A readable copy of <b>{chatTitle}</b> — every message you can still see, with
            the pictures embedded so the file opens on its own.
          </div>

          <div className="ci-pickrow">
            <span className="ci-picklabel">Include</span>
            <div className="ci-chips">
              <button className={`ci-chip ${fmt.html ? 'on' : ''}`} onClick={() => setFmt({ ...fmt, html: !fmt.html })}>
                {fmt.html && <Check width={11} height={11} />} Web page (.html)
              </button>
              <button className={`ci-chip ${fmt.txt ? 'on' : ''}`} onClick={() => setFmt({ ...fmt, txt: !fmt.txt })}>
                {fmt.txt && <Check width={11} height={11} />} Plain text (.txt)
              </button>
            </div>
          </div>

          <button className="lg-go" disabled={busy || (!fmt.html && !fmt.txt)} onClick={go}>
            {busy ? 'Preparing…' : 'Export & download'}
          </button>

          {done && (
            <div className="ex-done">
              <Check width={14} height={14} />
              Exported {done.messages} messages · {humanBytes(done.bytes)}
              {done.files > 1 ? ` · ${done.files} files` : ''}
            </div>
          )}
          {err && <div className="pm-err">{err}</div>}

          <div className="ex-note">
            The phone exports a ZIP with the media files themselves, because it holds them
            on disk. In a browser the media lives in the cloud, so this exports the
            transcript with its thumbnails instead — the file never leaves your computer
            either way.
          </div>

          {/* ── history ── */}
          <div className="ex-hist-head">
            <span className="ci-picklabel">Export history</span>
            <div className="ci-chips">
              <button className={`ci-chip ${scope === 'this' ? 'on' : ''}`} onClick={() => setScope('this')}>This chat</button>
              <button className={`ci-chip ${scope === 'all' ? 'on' : ''}`} onClick={() => setScope('all')}>All chats</button>
            </div>
          </div>

          <div className="ng-results">
            {shown.length === 0 && <div className="ci-empty">No exports yet.</div>}
            {shown.map((r) => (
              <div className="ng-row ex-row" key={r.id}>
                <span className="ex-ico"><Doc width={16} height={16} /></span>
                <span className="ng-row-who">
                  <b>{scope === 'all' ? (r.chatName || 'Chat') : when(r.at)}</b>
                  <i>
                    {scope === 'all' ? `${when(r.at)} · ` : ''}
                    {humanBytes(r.bytes)}
                    {r.files > 1 ? ` · ${r.files} files` : ''}
                    {r.format ? ` · ${String(r.format).toUpperCase()}` : ''}
                    {r.platform ? ` · ${r.platform === 'web' ? 'Web' : 'Phone'}` : ''}
                  </i>
                </span>
                <button
                  className="icon-btn ex-del" title="Remove from history"
                  onClick={() => deleteExportRecord(myUid, r.id).catch(() => {})}
                >
                  <Trash width={14} height={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="ng-foot">
          <span className="ng-count">History is shared with your phone</span>
          <button className="lg-go slim" onClick={onClose}>Done</button>
        </div>
      </div>
    </div>
  );
}
